/* One item in the browse grid: image first, title over it.

   A port of v1's `.thumb` (app/static/app.css) rather than a redesign — ADR 0002
   fixed Library's visual design as v1's. What is NOT ported is the 3-column grid
   it sat in, which was a phone figure; the column count lives in g-library and is
   fluid there.

   Kept separate from item-row rather than folded into one component with a
   `variant` prop (ADR 0002 §8). They lead with different things — a picture here,
   a matched sentence there — so a merged component would be a switch statement
   wearing a costume.

   Presentational: emits `open`, and the workspace owns the playlist. */

import { LitElement, html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { noCallout, shadowReset, suppressCallout } from '../../design/base.js';
import { apiUrl } from '../../core/api.js';
import { clean, fmtDuration } from '../../../../packages/core/index.js';
import type { Item } from '../../core/types.js';

@customElement('item-tile')
export class ItemTile extends LitElement {
  static override styles = [
    shadowReset,
    noCallout,
    css`
      :host {
        display: block;
      }
      button {
        /* 3:4, not the source 9:16. A portrait reel cropped to 3:4 keeps its
           subject and costs the grid a third of its height — at ~1,700 items the
           scroll length is a real cost. Ported from v1. */
        position: relative;
        display: block;
        width: 100%;
        aspect-ratio: 3 / 4;
        overflow: hidden;
        padding: 0;
        border: 0;
        border-radius: var(--radius-md);
        /* Also the placeholder for a missing thumbnail: ~4% of the library has
           none, and an empty tile reading as a hole in the grid is worse than a
           filled one with just a title. */
        background: var(--surface);
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
      }
      button {
        transition: filter 120ms ease;
      }
      button:active {
        filter: brightness(0.85);
      }
      button:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }
      /* Square on the phone grid: the tiles tile, edge to edge and 2px apart,
         and a rounded corner at that spacing reads as a rendering fault rather
         than a card. Corners belong to the desktop grid, where an 8px gap over
         the warm background genuinely is a set of cards (ADR 0009, Library
         pass). */
      @media (max-width: 959px) {
        button {
          border-radius: 0;
        }
        /* Nothing over the image on the phone grid. At 118px a title ellipsised
           to ~13 characters identifies nothing, and its scrim costs the bottom
           third of a thumbnail that is now the only thing distinguishing one
           tile from the next — the overlay stopped paying for itself when the
           tile shrank. Three columns of clean frames is the reel-grid idiom.

           The button keeps its aria-label, so the tile is still named for
           assistive tech and still findable by voice control; only the painted
           text goes. That is also why this hides them rather than skipping the
           render — one rule, and the accessible name is never in question. */
        .tt,
        .dur {
          display: none;
        }
      }
      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        /* Faded in on load: a grid of images decoding at different moments pops
           row by row, and the fade turns that into one settle. */
        opacity: 0;
        transition: opacity 300ms ease;
      }
      img.ld {
        opacity: 1;
      }
      /* Both overlays sit on the image, so they are fixed light-on-scrim rather
         than themed — a dark-theme token over a bright photo is unreadable. */
      .dur {
        position: absolute;
        right: var(--space-1);
        /* Clear of the title band below it. */
        bottom: 26px;
        padding: 0 var(--space-1);
        border-radius: var(--radius-sm);
        background: rgb(0 0 0 / 0.62);
        color: #fff;
        font-family: var(--font-mono);
        font-size: 0.62rem;
      }
      .tt {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        /* Tall top padding so the gradient has room to fade in above the text
           instead of banding across it. */
        padding: 18px var(--space-2) var(--space-2);
        background: linear-gradient(transparent, rgb(0 0 0 / 0.78));
        color: #f3efe6;
        font-size: 0.68rem;
        line-height: 1.3;
        /* One line. Two-line titles at this size turn the grid into text. */
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `,
  ];

  @property({ attribute: false }) item!: Item;

  @query('img') private img?: HTMLImageElement;

  /**
   * Catch the image that was already decoded before the listener existed.
   *
   * A cached thumbnail can finish decoding between the src binding and the point
   * the `load` handler goes live; the event never reaches us and the tile stays
   * at opacity 0, permanently blank while holding a complete image. `load` still
   * handles every image that decodes later, including the lazy ones down-grid.
   *
   * A 404 (the thumbnail download failed at ingest — ~40% of the local copy)
   * leaves `complete` true with naturalWidth 0, so it stays transparent and the
   * tile shows its --surface placeholder and title. That is the wanted outcome,
   * not a broken-image glyph, so it is deliberately not special-cased.
   */
  override firstUpdated(): void {
    if (this.img?.complete) this.img.classList.add('ld');
  }

  override render() {
    const it = this.item;
    if (!it) return html``;
    const title = clean(it.title) || 'Untitled';
    return html`
      <button
        aria-label=${title}
        @contextmenu=${suppressCallout}
        @click=${() => this.dispatchEvent(
          new CustomEvent('open', { bubbles: true, composed: true }),
        )}
      >
        ${it.thumb
          ? html`<img
              loading="lazy"
              src=${apiUrl(it.thumb)}
              alt=""
              @load=${(e: Event) => (e.target as HTMLElement).classList.add('ld')}
            />`
          : ''}
        ${it.duration_s ? html`<span class="dur">${fmtDuration(it.duration_s)}</span>` : ''}
        <span class="tt">${title}</span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'item-tile': ItemTile;
  }
}
